(() => {
  'use strict';

  class FreeShippingProgress extends HTMLElement {
    constructor() {
      super();

      this.threshold = Number(this.dataset.threshold || 0);
      this.message = this.querySelector('[data-free-shipping-message]');
      this.remaining = this.querySelector('[data-free-shipping-remaining]');
      this.fill = this.querySelector('[data-free-shipping-fill]');
      this.track = this.querySelector('[data-free-shipping-track]');

      this.abortController = null;
      this.updateTimeout = null;
    }

    connectedCallback() {
      this.bindEvents();
      this.updateFromCart();
    }

    disconnectedCallback() {
      this.abortController?.abort();

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
    }

    bindEvents() {
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      /*
       * Horizon / Shopify cart updates can be triggered by different
       * components and applications. Listen to common cart events.
       */
      document.addEventListener('cart:updated', () => {
        this.scheduleUpdate();
      }, { signal });

      document.addEventListener('cart-updated', () => {
        this.scheduleUpdate();
      }, { signal });

      document.addEventListener('cart:change', () => {
        this.scheduleUpdate();
      }, { signal });

      document.addEventListener('CartAddEvent', () => {
        this.scheduleUpdate();
      }, { signal });

      /*
       * Catch cart updates triggered by native fetch requests.
       * This is useful when Horizon or a third-party app updates
       * the cart without dispatching a custom event.
       */
      this.interceptFetch();
    }

    interceptFetch() {
      if (window.__freeShippingFetchIntercepted) {
        return;
      }

      window.__freeShippingFetchIntercepted = true;

      const originalFetch = window.fetch;

      window.fetch = async (...args) => {
        const response = await originalFetch(...args);

        try {
          const requestUrl = args[0] instanceof Request
            ? args[0].url
            : String(args[0]);

          const isCartRequest =
            requestUrl.includes('/cart/add') ||
            requestUrl.includes('/cart/change') ||
            requestUrl.includes('/cart/update') ||
            requestUrl.includes('/cart/clear');

          if (isCartRequest) {
            document.dispatchEvent(
              new CustomEvent('free-shipping:cart-updated')
            );
          }
        } catch (error) {
          // Do not interfere with the original request.
        }

        return response;
      };

      document.addEventListener('free-shipping:cart-updated', () => {
        document
          .querySelectorAll('[data-free-shipping-progress]')
          .forEach((progressBar) => {
            progressBar.scheduleUpdate();
          });
      });
    }

    scheduleUpdate() {
      clearTimeout(this.updateTimeout);

      this.updateTimeout = setTimeout(() => {
        this.updateFromCart();
      }, 100);
    }

    async updateFromCart() {
      try {
        const response = await fetch(
          `${window.Shopify.routes.root}cart.js`,
          {
            headers: {
              Accept: 'application/json'
            },
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          throw new Error('Unable to fetch cart');
        }

        const cart = await response.json();

        this.updateProgress(cart.total_price);
      } catch (error) {
        console.warn(
          '[Free Shipping Progress]',
          error
        );
      }
    }

    updateProgress(cartTotal) {
      const remainingAmount = Math.max(
        this.threshold - cartTotal,
        0
      );

      const percentage = Math.min(
        (cartTotal / this.threshold) * 100,
        100
      );

      const formattedRemaining = this.formatMoney(
        remainingAmount
      );

      if (remainingAmount > 0) {
        this.message.innerHTML = `
          You're only
          <strong data-free-shipping-remaining>
            ${formattedRemaining}
          </strong>
          away from free shipping
        `;
      } else {
        this.message.innerHTML = `
          🎉 Congrats! You've unlocked free shipping!
        `;
      }

      this.fill.style.width = `${percentage}%`;

      this.track.setAttribute(
        'aria-valuenow',
        Math.round(percentage)
      );

      this.classList.toggle(
        'is-complete',
        remainingAmount <= 0
      );
    }

    formatMoney(cents) {
      const currency =
        this.dataset.currency ||
        Shopify.currency?.active ||
        'USD';

      return new Intl.NumberFormat(
        document.documentElement.lang || 'en',
        {
          style: 'currency',
          currency,
          minimumFractionDigits: 2
        }
      ).format(cents / 100);
    }
  }

  if (!customElements.get('free-shipping-progress')) {
    customElements.define(
      'free-shipping-progress',
      FreeShippingProgress
    );
  }
})();